<template>
  <div
    v-if="!item.hidden && hasChildren"
    class="menu-wrapper wr"
  >
    <template v-if="showSingleChild">
      <app-link :to="resolvePath(onlyOneChild.path)">
        <el-menu-item
          :id="onlyOneChild.meta ? 'menu' + onlyOneChild.meta.title : ''"
          :index="resolvePath(onlyOneChild.path)"
          :class="{'submenu-title-noDropdown':!isNest}"
        >
          <!-- La pastille est ancrée à l'icône, pas au libellé : elle garde la
               même place barre dépliée ou repliée, et n'a plus besoin d'être
               repositionnée en mode icônes.
               onlyOneChild reprend le meta du parent quand le module n'a pas
               d'enfant visible : une seule lecture couvre les deux cas. -->
          <i class="menu-icon">
            <Icon
              :icon="onlyOneChild.meta?.icon || item.meta?.icon"
              width="20"
            />
            <span
              v-if="badgeCount(onlyOneChild)"
              class="menu-badge"
            >{{ badgeLabel(badgeCount(onlyOneChild)) }}</span>
          </i>
          <template #title>
            <span>{{ displayTitle(onlyOneChild) }}</span>
          </template>
        </el-menu-item>
      </app-link>
    </template>

    <!-- Menu avec sous-éléments -->
    <el-sub-menu
      v-else
      :index="resolvePath(item.path)"
    >
      <template #title>
        <div
          class="submenu-title-inner"
          @click="goTo(item.path)"
        >
          <!-- Groupe replié : la pastille de l'enfant remonte sur le parent,
               sinon la nouveauté serait invisible tant que le sous-menu
               n'est pas déplié. Le CSS l'efface une fois le sous-menu ouvert :
               l'enfant porte déjà le même chiffre, le redire est du bruit. -->
          <i class="menu-icon">
            <Icon
              :icon="item.meta?.icon"
              width="20"
            />
            <span
              v-if="badgeCountDescendants(item)"
              class="menu-badge"
            >{{ badgeLabel(badgeCountDescendants(item)) }}</span>
          </i>
          <span>{{ item.meta?.title }}</span>
        </div>
      </template>

      <!-- Enfants avec sous-menu -->
      <SidebarItem
        v-for="child in childrenWithSubMenu"
        :key="child.path"
        :item="child"
        :is-nest="true"
        :icon="child.meta?.icon"
        :base-path="resolvePath(child.path)"
        class="nest-menu"
      />

      <!-- Enfants simples -->
      <app-link
        v-for="child in childrenWithoutSubMenu"
        :key="'link-' + child.path"
        :to="resolvePath(child.path)"
      >
        <el-menu-item :index="resolvePath(child.path)">
          <i class="menu-icon">
            <Icon
              :icon="child.meta?.icon"
              width="20"
            />
            <span
              v-if="badgeCount(child)"
              class="menu-badge"
            >{{ badgeLabel(badgeCount(child)) }}</span>
          </i>
          <template #title>
            <span>{{ child.meta?.title }}</span>
          </template>
        </el-menu-item>
      </app-link>
    </el-sub-menu>

    <el-divider
      v-if="isGroup"
      class="my-divider"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { ElSubMenu, ElMenuItem, ElDivider } from 'element-plus';
import { Icon } from '@iconify/vue';
import path from 'path';
import { isExternal } from '@/utils/validate';
import { isExtranetContext } from '@/api/clientApiAdapter';
import AppLink from './Link.vue';
import store from '@/store';

// Props
const props = defineProps({
  item: { type: Object, required: true },
  isNest: { type: Boolean, default: false },
  basePath: { type: String, default: '' },
});

// Computed
const visibleChildren = computed(() => props.item.children?.filter(c => !c.hidden) || []);
const onlyOneChild = computed(() => {
  if (visibleChildren.value.length === 1) return visibleChildren.value[0];
  if (visibleChildren.value.length === 0) return { ...props.item, path: '', noShowingChildren: true };
  return null;
});
const showSingleChild = computed(() => {
  if (props.item.alwaysShow) return false;
  return onlyOneChild.value && visibleChildren.value.length <= 1;
});
// Les groupes arrivent déjà mis à plat par `menuTree` : il ne reste ici que des
// modules et leurs pages.
const hasChildren = computed(() => props.item.children?.length > 0);
const isGroup = computed(() => props.item.children && !showSingleChild.value);

// Séparer enfants avec et sans sous-menu
const childrenWithSubMenu = computed(() => visibleChildren.value.filter(c => c.children && c.children.length > 0));
const childrenWithoutSubMenu = computed(() => visibleChildren.value.filter(c => !c.children || c.children.length === 0));

// ── Pastilles de nouveauté ───────────────────────────────────────────────────
// Une route déclare `meta.badgeKey` ; le compteur correspondant vient du store
// (rafraîchi par le polling de useNotifications). Ces compteurs sont COLLECTIFS :
// une facture ouverte par un membre du service éteint la pastille de toute
// l'équipe — contrairement à la cloche, qui reste personnelle.
const menuBadges = computed(() => store.getters['notifications/menuBadges'] || {});

const badgeCount = (route) => {
  const key = route?.meta?.badgeKey;
  return key ? (menuBadges.value[key] || 0) : 0;
};

// Somme des pastilles d'un sous-arbre : un groupe replié doit signaler la
// nouveauté de ses enfants.
const badgeCountDescendants = (route) => {
  const propre = badgeCount(route);
  const enfants = (route?.children || []).reduce((total, c) => total + badgeCountDescendants(c), 0);
  return propre + enfants;
};

// La pastille tient sur deux caractères : au-delà de 99, le compte exact ne
// se lit plus dans un menu, il se lit dans la liste.
const badgeLabel = (n) => (n > 99 ? '99+' : String(n));

// Titre adapté au contexte (extranet renomme "Dashboard" → "Mon compte")
const displayTitle = (child) => {
  const title = child.meta?.title || '';
  if (title === 'Dashboard' && isExtranetContext(store)) return 'Mon compte';
  return title;
};

// Methods
const resolvePath = (routePath) => isExternal(routePath) ? routePath : path.resolve(props.basePath, routePath);

const goTo = (route) => {
  if (!route) return console.warn('Route invalide', route);

  const match = route.match(/\/extranet\/grp\/(\d+)$/);
  if (!match) return;

  const id = match[1];
  const cible = '/extranet/dashGroup/' + id;

  if (cible !== route) window.$router?.push(cible);
};
</script>

<style scoped>
.el-menu-item {
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Pas de gap : l'icône porte déjà sa marge (sidebar.scss). Les deux cumulés
   décalaient le libellé des sous-menus de 10px par rapport aux entrées simples. */
.submenu-title-inner {
  display: flex;
  align-items: center;
}

.my-divider {
  margin: 0 !important;
}

/* Enveloppe de l'icône. Un <i> et non un <span> : barre repliée, sidebar.scss
   masque TOUT span enfant direct d'un item de menu pour escamoter le libellé —
   un wrapper en <span> emporterait l'icône avec lui. */
.menu-icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 10px;
  font-style: normal;
}

/* Pastille de nouveauté : un compteur discret au bout de la ligne, pas une
   gélule posée sur l'icône. Elle se teinte d'après le texte de sa ligne
   (`currentColor`) : le menu a trois fonds — module sombre, sous-menu blanc,
   ligne active turquoise — et une couleur fixe jurait sur deux d'entre eux.
   Ancrée à la ligne (`.el-menu-item` et `.el-sub-menu__title` sont en
   `position: relative`), d'où `.menu-icon` laissé hors positionnement.
   Un <span> et non <el-badge>, qui rend un <div> au comportement imprévisible
   dans un menu (voir l'historique du mode replié). */
.menu-badge {
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: color-mix(in srgb, currentcolor 14%, transparent);
  color: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

/* Titre de sous-menu : la flèche occupe déjà le bord droit. */
.el-sub-menu__title .menu-badge {
  right: 40px;
}

/* Sous-menu ouvert : l'enfant porte le même chiffre, deux pastilles pour une
   seule nouveauté font du bruit. Combinateurs ENFANT jusqu'au titre : en
   descendant, la règle effacerait aussi la pastille des sous-menus restés
   fermés à l'intérieur, dont aucun enfant visible ne reprend le compte.
   Barre repliée, le sous-menu s'ouvre dans un popper dont le `ul` ne porte pas
   `el-menu--collapse` : la règle y joue donc aussi, au niveau du popper. */
.el-menu:not(.el-menu--collapse) .el-sub-menu.is-opened > .el-sub-menu__title > .submenu-title-inner .menu-badge {
  opacity: 0;
}

/* Barre repliée : plus de place pour un chiffre, un point sur le coin de
   l'icône dit qu'il y a du nouveau — le compte se lit en dépliant. */
.el-menu--collapse .menu-icon {
  position: relative;
}

.el-menu--collapse .menu-badge {
  top: -2px;
  right: -3px;
  transform: none;
  min-width: 0;
  width: 8px;
  height: 8px;
  padding: 0;
  border-radius: 50%;
  background: #6ed6ce;
  font-size: 0;
}
</style>
